/**
 * @module ContainerTriageParts
 * @purpose Presentational sub-components for the artefact-triage panel:
 *   header, loading/error/needs-inventory states, kind filter, group/item
 *   rows, and the submitted/footer chrome.
 * @depends types/evidence-containers, types/ingestion, container-triage.helpers
 * @dependedOnBy ContainerTriagePanel
 *
 * @rules
 * - State: all state passed via props from the parent -- purely presentational
 * - Errors: role="alert" on error banners
 * - Theming: Tailwind slate/border classes for chrome, var(--accent) for
 *   primary actions (matches upload-receipt-modal.parts precedent)
 */

import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { AlertCircle, CheckCircle, RefreshCw, X } from 'lucide-react'
import type {
  ContainerInventoryItem,
  InventoryDiscoveryStatus,
  InventoryGroup,
  InventoryItemKind,
} from '../../../types/evidence-containers'
import type { IngestionRunStart } from '../../../types/ingestion'
import { groupCheckState, isAlreadyIngested } from './container-triage.helpers'

const KIND_LABEL_KEYS: Record<InventoryItemKind, string> = {
  parsed_source: 'containerTriage.kindParsedSource',
  raw_exhibit: 'containerTriage.kindRawExhibit',
  collector_metadata: 'containerTriage.kindCollectorMetadata',
  unsupported: 'containerTriage.kindUnsupported',
}

interface PanelHeaderProps {
  onClose: () => void
  t: TFunction<'cases'>
}

export function PanelHeader({ onClose, t }: PanelHeaderProps): ReactElement {
  return (
    <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('containerTriage.title')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('containerTriage.subtitle')}</p>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        aria-label={t('containerTriage.close')}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

export function PanelLoadingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400" role="status" aria-busy="true">
      {t('containerTriage.loading')}
    </div>
  )
}

interface LoadErrorStateProps {
  message: string
  onRetry: () => void
  t: TFunction<'cases'>
}

export function LoadErrorState({ message, onRetry, t }: LoadErrorStateProps): ReactElement {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
      <div className="flex-1">
        <p className="text-sm text-red-700 dark:text-red-300">{t('containerTriage.loadError')}</p>
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{message}</p>
        <button type="button" onClick={onRetry} className="mt-2 text-xs font-medium text-red-700 hover:underline dark:text-red-300">
          {t('containerTriage.retry')}
        </button>
      </div>
    </div>
  )
}

interface NeedsInventoryStateProps {
  status: InventoryDiscoveryStatus
  onRunInventory: () => void
  t: TFunction<'cases'>
}

export function NeedsInventoryState({ status, onRunInventory, t }: NeedsInventoryStateProps): ReactElement {
  if (status === 'in_progress') {
    return (
      <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400" role="status" aria-busy="true">
        {t('containerTriage.inventoryInProgress')}
      </p>
    )
  }
  const body = status === 'failed' ? t('containerTriage.inventoryFailedBody') : t('containerTriage.needsInventoryBody')
  return (
    <div className="rounded-lg border border-slate-200 p-4 text-center dark:border-slate-700">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('containerTriage.needsInventoryTitle')}</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{body}</p>
      <button
        type="button"
        onClick={onRunInventory}
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-medium text-white"
        style={{ background: 'var(--accent)' }}
      >
        <RefreshCw className="h-3.5 w-3.5" />
        {t('containerTriage.runInventory')}
      </button>
    </div>
  )
}

interface KindFilterSelectProps {
  value: InventoryItemKind | null
  onChange: (kind: InventoryItemKind | null) => void
  t: TFunction<'cases'>
}

export function KindFilterSelect({ value, onChange, t }: KindFilterSelectProps): ReactElement {
  return (
    <div>
      <label htmlFor="triage-kind-filter" className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
        {t('containerTriage.kindFilterLabel')}
      </label>
      <select
        id="triage-kind-filter"
        value={value ?? ''}
        onChange={(e) => { onChange(e.target.value ? (e.target.value as InventoryItemKind) : null) }}
        className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">{t('containerTriage.kindFilterAll')}</option>
        {Object.entries(KIND_LABEL_KEYS).map(([kind, key]) => (
          <option key={kind} value={kind}>{t(key as never)}</option>
        ))}
      </select>
    </div>
  )
}

interface GroupHeaderProps {
  group: InventoryGroup
  selectedKeys: ReadonlySet<string>
  onToggleGroup: (group: InventoryGroup) => void
  t: TFunction<'cases'>
}

export function GroupHeader({ group, selectedKeys, onToggleGroup, t }: GroupHeaderProps): ReactElement {
  const checkState = groupCheckState(group, selectedKeys)
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-1.5 pt-3 dark:border-slate-800">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          aria-label={t('containerTriage.selectAllInGroup')}
          checked={checkState === 'all'}
          ref={(el) => {
            if (el) el.indeterminate = checkState === 'some'
          }}
          onChange={() => { onToggleGroup(group) }}
        />
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{group.displayName}</span>
        {!group.parserAvailable && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
            {t('containerTriage.noParserAvailable')}
          </span>
        )}
      </div>
    </div>
  )
}

interface ItemRowProps {
  item: ContainerInventoryItem
  selected: boolean
  reingest: boolean
  onToggle: (item: ContainerInventoryItem) => void
  t: TFunction<'cases'>
}

export function ItemRow({ item, selected, reingest, onToggle, t }: ItemRowProps): ReactElement {
  const alreadyIngested = isAlreadyIngested(item)
  return (
    <div className="flex items-center gap-2 py-1 pl-6 text-sm">
      <input
        type="checkbox"
        checked={selected}
        onChange={() => { onToggle(item) }}
        aria-label={item.displayName}
      />
      <span className="flex-1 truncate text-slate-700 dark:text-slate-300" title={item.fidelityNote ?? undefined}>
        {item.displayName}
      </span>
      {item.needsReingest && (
        <span className="text-[10px] text-amber-600 dark:text-amber-400">{t('containerTriage.needsReingestFlag')}</span>
      )}
      {alreadyIngested && item.lastIngestedAt && (
        <span className="text-[10px] text-slate-400 dark:text-slate-500">
          {t('containerTriage.ingestedMarker', {
            date: new Date(item.lastIngestedAt).toLocaleDateString(),
            count: item.lastRowCount ?? 0,
          })}
        </span>
      )}
      {reingest && (
        <span className="text-[10px] font-medium" style={{ color: 'var(--accent)' }}>
          {t('containerTriage.needsReingestFlag')}
        </span>
      )}
    </div>
  )
}

export function EmptyGroupsState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
      {t('containerTriage.emptyGroups')}
    </p>
  )
}

interface SubmittedStateProps {
  run: IngestionRunStart
  onClose: () => void
  t: TFunction<'cases'>
}

export function SubmittedState({ run, onClose, t }: SubmittedStateProps): ReactElement {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/50">
      <CheckCircle className="h-5 w-5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
      <div className="flex-1">
        <p className="text-sm text-emerald-700 dark:text-emerald-300">{t('containerTriage.submitted')}</p>
        <p className="text-xs text-emerald-600 dark:text-emerald-400">
          {t('containerTriage.submittedBody', { status: run.status })}
        </p>
      </div>
      <button type="button" onClick={onClose} className="text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-300">
        {t('containerTriage.close')}
      </button>
    </div>
  )
}

interface SubmitErrorBannerProps {
  message: string
  t: TFunction<'cases'>
}

export function SubmitErrorBanner({ message, t }: SubmitErrorBannerProps): ReactElement {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
      <p className="text-sm text-red-700 dark:text-red-300">{message || t('containerTriage.submitError')}</p>
    </div>
  )
}

interface SelectionFooterProps {
  selectedCount: number
  submitting: boolean
  onSubmit: () => void
  onClose: () => void
  t: TFunction<'cases'>
}

export function SelectionFooter({ selectedCount, submitting, onSubmit, onClose, t }: SelectionFooterProps): ReactElement {
  return (
    <div className="flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-700">
      <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
        {t('containerTriage.selectedCount', { count: selectedCount })}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
        >
          {t('containerTriage.close')}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || selectedCount === 0}
          className="rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          style={{ background: 'var(--accent)' }}
        >
          {submitting ? t('containerTriage.submitting') : t('containerTriage.submit')}
        </button>
      </div>
    </div>
  )
}
